/**
 * Convert between the in-app DemoRow[] shape and the compact ChannelEvent
 * format used by the AI generation API.
 *
 * The AI side talks in events (step + note + optional metadata) instead of
 * dense row arrays so the wire payload stays small. These helpers translate
 * both directions.
 */

import type { DemoCell, DemoNoiseCell, DemoRow, DemoSong } from '../audio/demo-song';
import type { ChannelName } from './keyboard';

export interface ChannelEvent {
  step: number;
  note?: string | null;
  duration?: number;
  instrument?: number;
  volume?: number;
  effect?: string | null;
}

export type EventChannel = 'pulse1' | 'pulse2' | 'triangle' | 'noise';

const EVENT_CHANNELS: ReadonlyArray<EventChannel> = [
  'pulse1',
  'pulse2',
  'triangle',
  'noise',
];

export function isEventChannel(ch: ChannelName): ch is EventChannel {
  return EVENT_CHANNELS.includes(ch as EventChannel);
}

/**
 * Convert one channel of a row array to compact events. Rows where the
 * channel is empty produce no events; rows with `note=null` (or undefined
 * note) on a present cell produce no event either.
 */
export function patternToChannelEvents(rows: DemoRow[], channel: EventChannel): ChannelEvent[] {
  const out: ChannelEvent[] = [];
  for (let step = 0; step < rows.length; step++) {
    const row = rows[step];
    if (!row) continue;
    if (channel === 'pulse1' || channel === 'pulse2') {
      const cell = row[channel] as DemoCell | undefined;
      if (!cell || cell.note == null) continue;
      const ev: ChannelEvent = { step, note: cell.note === 'OFF' ? null : cell.note };
      if (cell.volume !== undefined) ev.volume = cell.volume;
      // We model duty as the "instrument" hint (0-3 ↔ 12.5/25/50/75%).
      if (cell.duty !== undefined) ev.instrument = cell.duty;
      out.push(ev);
    } else if (channel === 'triangle') {
      const cell = row.triangle;
      if (!cell || cell.note == null) continue;
      out.push({ step, note: cell.note === 'OFF' ? null : cell.note });
    } else if (channel === 'noise') {
      const cell = row.noise;
      if (!cell || cell.note == null) continue;
      const ev: ChannelEvent = {
        step,
        note: cell.note === 'OFF' ? null : 'on',
      };
      if (cell.volume !== undefined) ev.volume = cell.volume;
      out.push(ev);
    }
  }
  return out;
}

/** Helper: produce the full `existing_channels` payload for an AI request. */
export function patternToExistingChannels(
  rows: DemoRow[],
  exclude: EventChannel,
): Record<string, ChannelEvent[]> {
  const out: Record<string, ChannelEvent[]> = {};
  for (const ch of EVENT_CHANNELS) {
    if (ch === exclude) continue;
    const events = patternToChannelEvents(rows, ch);
    if (events.length > 0) out[ch] = events;
  }
  return out;
}

/**
 * Apply a sequence of channel events back into a pattern, replacing the
 * named channel and leaving every other channel untouched. Empty rows in
 * the result get the channel removed entirely.
 */
export function applyChannelEvents(
  rows: DemoRow[],
  channel: EventChannel,
  events: ChannelEvent[],
): DemoRow[] {
  // Index events by step for quick lookup, keeping the last one if there
  // are duplicates (defensive — the AI shouldn't emit duplicates).
  const byStep = new Map<number, ChannelEvent>();
  for (const ev of events) byStep.set(ev.step, ev);

  return rows.map((row, step) => {
    const ev = byStep.get(step);
    if (!ev) {
      // Drop any existing content on this channel.
      const next: DemoRow = { ...row };
      delete next[channel];
      return next;
    }
    return assignEventToCell(row, channel, ev);
  });
}

function assignEventToCell(row: DemoRow, channel: EventChannel, ev: ChannelEvent): DemoRow {
  const next: DemoRow = { ...row };
  if (channel === 'pulse1' || channel === 'pulse2') {
    const existing = (row[channel] ?? {}) as DemoCell;
    const cell: DemoCell = { ...existing };
    cell.note = ev.note === null || ev.note === undefined ? 'OFF' : ev.note;
    if (ev.volume !== undefined) cell.volume = clampInt(ev.volume, 0, 15);
    else if (cell.volume === undefined) cell.volume = 12;
    if (ev.instrument !== undefined) {
      cell.duty = clampInt(ev.instrument, 0, 3) as 0 | 1 | 2 | 3;
    } else if (cell.duty === undefined) {
      cell.duty = 1;
    }
    next[channel] = cell;
    return next;
  }
  if (channel === 'triangle') {
    next.triangle = { note: ev.note === null || ev.note === undefined ? 'OFF' : ev.note };
    return next;
  }
  if (channel === 'noise') {
    const existing = (row.noise ?? {}) as DemoNoiseCell;
    if (ev.note === null || ev.note === undefined) {
      next.noise = { note: 'OFF' };
    } else {
      next.noise = {
        note: 'on',
        periodIndex: existing.periodIndex ?? 5,
        mode: existing.mode ?? 'long',
        volume: ev.volume !== undefined ? clampInt(ev.volume, 0, 15) : (existing.volume ?? 8),
        decayMs: existing.decayMs,
      };
    }
    return next;
  }
  return next;
}

function clampInt(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.round(v)));
}

/** Replace the channel in the song's pattern at the given index. */
export function replaceChannelInSong(
  song: DemoSong,
  patternIndex: number,
  channel: EventChannel,
  events: ChannelEvent[],
): DemoSong {
  const target = song.patterns[patternIndex];
  if (!target) {
    throw new Error(`patternIndex ${patternIndex} out of range.`);
  }
  return {
    ...song,
    patterns: song.patterns.map((p, i) =>
      i === patternIndex ? applyChannelEvents(p, channel, events) : p,
    ),
  };
}
